/* eslint-disable react/jsx-props-no-spreading */
import React, { CSSProperties } from 'react';
import ReactSelect, { ActionMeta, GroupBase, OnChangeValue, StylesConfig } from 'react-select';
import styled, { css } from 'styled-components';
import { InputErrors } from './WithErrors';

type Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> = {
  disabled?: boolean;
  required?: boolean;
  setSelected?: (newValue: OnChangeValue<Option, IsMulti>, actionMeta: ActionMeta<Option>) => void;
} & Omit<React.ComponentProps<typeof InputErrors>, 'children'> &
  React.ComponentProps<typeof ReactSelect<Option, IsMulti, Group>>;

type StandardOption<T> = { label: string; value: T | null } & ColorProp;

type Color = CSSProperties['color'];
type ColorProp = { color?: Color };

const defaultSelectStyles = css`
  line-height: 1.9;
  vertical-align: middle;
  padding: 0;
  text-indent: 8px;
  border: none;
  background-color: #f5f5f5;
  background-image: none;
  box-shadow: none;
  foreground-color: red;
  display: inline-block;
  width: 100%;
`;

const SelectWrapper = styled.div`
  ${defaultSelectStyles}
`;

const colorOf = (data: unknown) =>
  typeof data === 'object' && !!data && 'color' in data && typeof data.color === 'string'
    ? data.color
    : undefined;

const optionColorOf = ({
  data,
  isDisabled,
  isSelected,
}: {
  data: unknown;
  isDisabled: boolean;
  isSelected: boolean;
}) => {
  if (isDisabled) return '#ccc';
  return isSelected ? 'white' : colorOf(data);
};

const colorStyles = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>
>(): StylesConfig<Option, IsMulti, Group> => ({
  option: (styles, props) => ({
    ...styles,
    color: optionColorOf(props),
  }),
  singleValue: (styles, { data }) => ({ ...styles, color: colorOf(data) }),
});

export const addNullOption = <T,>(opts: StandardOption<T>[], label = '指定なし') =>
  ([{ label, value: null, color: 'gray' }] as StandardOption<T>[]).concat(opts);

const MySelect = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  required,
  disabled,
  errors,
  onChange,
  setSelected,
  styles,
  value,
  fullWidth,
  ...rest
}: Props<Option, IsMulti, Group>) => (
  <InputErrors errors={errors} fullWidth={fullWidth}>
    <ReactSelect
      styles={styles || colorStyles<Option, IsMulti, Group>()}
      onChange={onChange || setSelected}
      value={value}
      {...rest}
    />
    {!disabled && required && (
      <input
        tabIndex={-1}
        autoComplete="off"
        style={{ opacity: 0, height: 0 }}
        value={value?.toString()}
        // onChange={onChange}
        required={required}
      />
    )}
  </InputErrors>
);

export const SelectT = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: React.ComponentPropsWithoutRef<typeof MySelect<Option, IsMulti, Group>>
) => (
  <SelectWrapper>
    <MySelect {...props} />
  </SelectWrapper>
);

/**
 * To preserve the types of options, (e.g. to use filterOptions), use Select instead
 * @see SelectT
 */
export default styled(MySelect)`
  ${defaultSelectStyles}
` as typeof MySelect;

type ThreeStateSelectProps = {
  value: boolean | null;
  trueLabel: string;
  falseLabel: string;
  unselectedLabel: string;
  setValue?: (value: boolean | null) => void;
  className?: string;
};

type BooleanOption = StandardOption<boolean>;

const optionToBool = (opt: BooleanOption) => opt.value;

const ThreeStateSelectUnstyled = ({
  trueLabel,
  falseLabel,
  unselectedLabel,
  value,
  setValue,
  className = '',
}: ThreeStateSelectProps) => {
  const unselectedOption = { value: null, label: unselectedLabel };
  const trueOption = { value: true, label: trueLabel };
  const falseOption = { value: false, label: falseLabel };

  const options: BooleanOption[] = [unselectedOption, trueOption, falseOption];

  const boolToOption = (b: boolean | null) => {
    switch (b) {
      case true:
        return trueOption;
      case false:
        return falseOption;
      default:
        return unselectedOption;
    }
  };

  return (
    <ReactSelect
      isMulti={false}
      className={className}
      options={options}
      value={boolToOption(value)}
      onChange={(selectedValue) => setValue?.(optionToBool(selectedValue ?? unselectedOption))}
    />
  );
};

export const ThreeStateSelect = styled(ThreeStateSelectUnstyled)``;
